import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// Copy a single exercise to another day inside the same program.
export function CopyExerciseToDayDialog({ exercise, weekDays, onConfirm, onCancel }) {
  const [target, setTarget] = useState(weekDays[0] ? `${weekDays[0].week}-${weekDays[0].day}` : "");
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-md" data-testid="copy-ex-to-day-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">COPY EXERCISE</DialogTitle>
          <DialogDescription className="text-xs text-text-secondary">Append &ldquo;{exercise?.name || "Exercise"}&rdquo; to another day in this program.</DialogDescription>
        </DialogHeader>
        {weekDays.length === 0 ? (
          <p className="text-sm text-text-secondary">Add another session in this program before copying.</p>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="label-tiny block mb-2">TARGET DAY</label>
              <select value={target} onChange={(e) => setTarget(e.target.value)} className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm" data-testid="copy-ex-target-day">
                {weekDays.map((d) => <option key={`${d.week}-${d.day}`} value={`${d.week}-${d.day}`}>Week {d.week} · Day {d.day} — {d.name || "(untitled)"}</option>)}
              </select>
            </div>
            <button onClick={() => onConfirm(target)} className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors w-full py-3 text-xs label-tiny tracking-widest" data-testid="copy-ex-confirm">COPY EXERCISE</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Copy an entire day (all sections/exercises) into another day slot.
// Only non-rest, non-source days are shown as targets.
export function CopyDayToDayDialog({ sourceDay, allDays, onConfirm, onCancel }) {
  const candidates = (allDays || []).filter(
    (d) => !(d.week === sourceDay.week && d.day === sourceDay.day) && !d.is_rest,
  ).sort((a, b) => (a.week - b.week) || (a.day - b.day));
  const [target, setTarget] = useState(candidates[0] ? `${candidates[0].week}-${candidates[0].day}` : "");
  const exerciseCount = (sourceDay?.exercises || []).length;
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="bg-surface-DEFAULT border-white/10 text-white max-w-md" data-testid="copy-day-to-day-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">COPY DAY</DialogTitle>
          <DialogDescription className="text-xs text-text-secondary">
            Append all {exerciseCount} exercise{exerciseCount === 1 ? "" : "s"} from
            {" "}<span className="!text-yellow-400">Week {sourceDay.week} · Day {sourceDay.day}</span>{" "}
            into another day. Existing exercises are preserved. Rest days are hidden.
          </DialogDescription>
        </DialogHeader>
        {candidates.length === 0 ? (
          <p className="text-sm text-text-secondary" data-testid="copy-day-no-targets">No other non-rest days in this program yet.</p>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="label-tiny block mb-2">TARGET DAY</label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm"
                data-testid="copy-day-target"
              >
                {candidates.map((d) => (
                  <option key={`${d.week}-${d.day}`} value={`${d.week}-${d.day}`}>
                    Week {d.week} · Day {d.day} — {d.name || "(untitled)"} · {(d.exercises || []).length} ex
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => onConfirm(target)}
              className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors w-full py-3 text-xs label-tiny tracking-widest"
              data-testid="copy-day-confirm"
            >
              APPEND DAY CONTENT
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
